const isVisible = (el: HTMLElement): boolean =>
    el.offsetWidth > 0 && el.offsetHeight > 0;

/**
 * Collects and returns all visible menu groups and items within the given container.
 * @param container
 */
export const getVisibleMenuItems = (
    container: HTMLElement
): HTMLUxIwsSfMenuItemElement[] => {
    if (!container || !container.querySelector) {
        return [];
    }
    return Array.from<HTMLUxIwsSfMenuItemElement>(
        container.querySelectorAll<HTMLUxIwsSfMenuItemElement>(
            "ux-iws-sf-menu-item"
        )
    ).filter((el) => isVisible(el)) as HTMLUxIwsSfMenuItemElement[];
};

const getHasSubmenu = (el: HTMLElement): boolean =>
    el?.getAttribute("aria-haspopup") === "true";

/**
 * Gets the parent menu group of the given element.
 * @param el - The element to check for a parent group.
 * @returns The parent menu group element or null if not found.
 */
export const getParentGroup = (
    el: HTMLElement
): HTMLUxIwsSfMenuItemElement | null => {
    return (
        el?.closest<HTMLUxIwsSfMenuItemElement>("ux-iws-sf-menu-item") || null
    );
};

/**
 * Calculates the level of the menu group based on its parent groups.
 * @returns The level of the menu group.
 */
export const getLevel = (el: HTMLElement): number => {
    let level = 0;
    let parentGroup = el.parentElement
        ? getParentGroup(el.parentElement)
        : null;
    while (parentGroup) {
        level++;
        parentGroup = parentGroup.parentElement
            ? getParentGroup(parentGroup.parentElement)
            : null;
    }
    return level;
};

/**
 * Handles arrow key navigation within a menu.
 * Focuses the next or previous menu group or item based on the arrow key pressed.
 * @param event {KeyboardEvent}
 * @param host {HTMLUxIwsSfMenuItemElement}
 */
export const handleArrowNavigation = (
    event: KeyboardEvent,
    host: HTMLUxIwsSfMenuItemElement
) => {
    if (
        !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)
    ) {
        return;
    }
    if (event.cancelable) {
        event.preventDefault();
    }
    const target = event.target as HTMLElement;
    const item = host;
    const menu = item.closest<HTMLElement>(`[role="menu"]`);
    const hasSubmenu: boolean = getHasSubmenu(target);
    const visibleItems = getVisibleMenuItems(menu);

    const focusNextItem = (
        items: HTMLUxIwsSfMenuItemElement[],
        offset: number
    ): void => {
        const currentIndex = items.indexOf(item);
        let nextIndex = currentIndex + offset;
        if (nextIndex < 0) {
            nextIndex = items.length - 1;
        } else if (nextIndex >= items.length) {
            nextIndex = 0;
        }
        items[nextIndex]?.setFocus();
    };

    if (event.key === "ArrowRight") {
        if (hasSubmenu) {
            item.expand();
            return;
        }
    }
    if (event.key === "ArrowLeft") {
        if (hasSubmenu) {
            item.expanded = false;
            return;
        }
    }
    if (event.key === "ArrowDown") {
        focusNextItem(visibleItems, 1);
        return;
    }
    if (event.key === "ArrowUp") {
        focusNextItem(visibleItems, -1);
        return;
    }
};

/**
 * Focuses the currently active menu item or the first child item.
 * @param host {HTMLElement}
 */
export const focusNextChild = (host: HTMLElement) => {
    const visibleItems = getVisibleMenuItems(host);
    let focusItem: HTMLUxIwsSfMenuItemElement | null = visibleItems.find(
        (el) => !!(el as HTMLUxIwsSfMenuItemElement).current
    );
    if (!focusItem) {
        focusItem = host.querySelector<HTMLUxIwsSfMenuItemElement>(
            "ux-iws-sf-menu-item"
        );
    }
    focusItem?.setFocus();
};

export const isFocusable = (el: HTMLUxIwsSfMenuItemElement): boolean => {
    if (el.current) {
        return true;
    }
    if (el.currentChild && !el.expanded) {
        return true;
    }
    const menu = el.closest<HTMLElement>(`[role="menu"], [role="menubar"]`);
    if (!menu) {
        return true;
    }
    const visibleItems = getVisibleMenuItems(menu);
    const currentVisibleItem = visibleItems.find((item) => !!item.current);
    if (currentVisibleItem) {
        return currentVisibleItem === el;
    }
    return visibleItems.indexOf(el) === 0;
};
